import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  Baby, Check, ChevronLeft, KeyRound, Link2, Plus, UserPlus, Users,
} from 'lucide-react';
import {
  createChild, redeemGuardianInvite, setChildGender, setParentPin, useChildRecitations, useHasPin,
  useManagedBy, useMyChildren, type Child,
} from '@core/firebase/children';
import {
  CardTitle, ChoicePills, EmptyState, IconTile, Notice, PageHeader, QuietPill, SoftCard, field, pill,
} from '@/components/PageKit';
import { GenderPrompt } from '@/components/GenderPrompt';
import type { Gender } from '@core/firebase/profile';
import { isValidDisplayName } from '@core/auth/displayName';
import { useAuth } from '@/contexts/AuthContext';
import { MIN_CHILD_AGE, MAX_CHILD_AGE } from '@core/auth/childAge';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/cn';
import { applyFormat, inputAttrs } from '@core/format/inputs';

/**
 * Where a parent creates and keeps their children's accounts.
 *
 * The consent tick is not a formality and not pre-ticked: it is the record
 * the server writes against a person who cannot consent for themselves, and
 * the account cannot be created without it.
 *
 * Redesign 2026-10 (board f1), at desktop width: one main action, «أضف طفلاً»;
 * the children as soft cards in a grid, each with its week and the way into
 * its progress; the PIN and following-by-code at the side — the PIN first
 * and ringed in lime while it is missing.
 */
export default function ChildrenPage() {
  const { identity } = useAuth();
  const { t } = useLanguage();
  const { children, loading } = useMyChildren(identity?.userId ?? null);
  const { hasPin } = useHasPin(identity?.userId ?? null);
  const { managedBy } = useManagedBy(identity?.userId ?? null);

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState<Gender | null>(null);
  const [country, setCountry] = useState('');
  const [grade, setGrade] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [created, setCreated] = useState(false);
  const [pin, setPin] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const [pinErrorKey, setPinErrorKey] = useState<string | null>(null);
  const [pinSaved, setPinSaved] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkErrorKey, setLinkErrorKey] = useState<string | null>(null);
  const [linked, setLinked] = useState(false);

  const reset = () => {
    setName(''); setAge(''); setGender(null); setCountry(''); setGrade('');
    setConsent(false);
  };

  const save = async () => {
    if (busy) return;
    if (!isValidDisplayName(name.trim())) { setErrorKey('children.needName'); return; }
    const n = Number(age);
    if (!Number.isInteger(n) || n < MIN_CHILD_AGE || n > MAX_CHILD_AGE) {
      setErrorKey('children.needAge'); return;
    }
    // Gender is optional (App Review 5.1.1(v)); a child without one is asked
    // about on their card below, where it is explained.
    // Refused here as well as recorded on the server: creating an account for
    // a child without their guardian saying so is the thing this prevents.
    if (!consent) { setErrorKey('children.needConsent'); return; }

    setBusy(true);
    setErrorKey(null);
    try {
      await createChild({
        displayName: name.trim(),
        age: n,
        ...(gender ? { gender } : {}),
        // Optional together, exactly as on a grown profile: a grade with no
        // country selects no curriculum.
        ...(country && grade ? { country, grade: Number(grade) } : {}),
      });
      reset();
      setAdding(false);
      setCreated(true);
    } catch (e) {
      const message = String((e as { message?: string })?.message ?? '');
      setErrorKey(message.includes('too-many-children') ? 'children.tooMany' : 'children.failed');
    } finally {
      setBusy(false);
    }
  };

  const savePin = async () => {
    if (pinBusy) return;
    if (!/^[0-9]{4}$/.test(pin)) { setPinErrorKey('children.pinFormat'); return; }
    setPinBusy(true);
    setPinErrorKey(null);
    try {
      await setParentPin(pin);
      setPin('');
      setPinSaved(true);
    } catch {
      setPinErrorKey('children.failed');
    } finally {
      setPinBusy(false);
    }
  };

  // A child holding the device is not a guardian: this page would offer her
  // "add a child" and a guardian PIN of her own. Guarded HERE rather than only
  // in the menu, because a deep link reaches the page without passing it.
  if (managedBy) return <Navigate to="/home" replace />;

  /*
   * م-3-01. Checked here as well as on the server so a mistyped code is
   * answered at once instead of after a round trip — and the SERVER is what
   * refuses a wrong one, since this check knows only the shape.
   */
  const CODE_SHAPE = /^[A-HJ-NP-Z2-9]{6}$/;

  const follow = async () => {
    if (linkBusy) return;
    const code = inviteCode.trim().toUpperCase().replace(/[\s-]/g, '');
    if (!CODE_SHAPE.test(code)) { setLinkErrorKey('children.linkBadCode'); return; }
    setLinkBusy(true);
    setLinkErrorKey(null);
    try {
      await redeemGuardianInvite(inviteCode.trim());
      setInviteCode('');
      setLinked(true);
    } catch (e) {
      // Named failures, because each one has a different thing to do about
      // it: ask for a new code, use a different one, or nothing at all.
      const message = String((e as { message?: string })?.message ?? '');
      setLinkErrorKey(
        message.includes('invite-expired') ? 'children.linkExpired'
          : message.includes('invite-used') ? 'children.linkUsed'
            : message.includes('already-a-guardian') ? 'children.linkAlready'
              : message.includes('too-many-guardians') ? 'children.linkTooMany'
                : message.includes('no-such-invite') || message.includes('invalid-code')
                  ? 'children.linkBadCode'
                  : 'children.linkFailed',
      );
    } finally {
      setLinkBusy(false);
    }
  };

  const pinCard = (
    /*
     * The way back from a child's session. Shown FIRST while it is missing,
     * because a parent who hands the device over without one is stranded in
     * their child's account; once set it moves to the side, quiet.
     */
    <SoftCard
      testId="pin-section"
      className={cn('space-y-4', !hasPin && 'ring-2 ring-lime')}
    >
      <CardTitle
        icon={<KeyRound />}
        trailing={hasPin ? (
          <span data-testid="pin-is-set" className="flex h-8 w-8 items-center justify-center rounded-full bg-lime text-accent-foreground">
            <Check className="h-4 w-4" aria-label="✓" />
          </span>
        ) : undefined}
      >
        {t('children.setPinTitle')}
      </CardTitle>
      <p className="text-sm text-helper">{t('children.setPinWhy')}</p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          data-testid="new-pin"
          value={pin}
          onChange={(e) => { setPin(applyFormat('pin', e.target.value)); setPinSaved(false); }}
          placeholder={t('children.pinPlaceholder')}
          type="password"
          {...inputAttrs('pin')}
          className={cn(field, 'w-36 text-center tracking-[0.5em]')}
        />
        {/*
          * The first call of a session can take seconds. A button that only
          * dims says nothing to a guardian watching it, who then presses
          * again — so it says it is working, and to a screen reader too.
          */}
        <button
          type="button"
          data-testid="save-pin"
          onClick={() => { void savePin(); }}
          disabled={pinBusy}
          aria-busy={pinBusy}
          className={cn(pill.base, pill.md, pill.lime)}
        >
          {pinBusy ? t('common.saving') : t('children.setPin')}
        </button>
      </div>
      {pinErrorKey && <Notice tone="danger" testId="pin-set-error">{t(pinErrorKey)}</Notice>}
      {pinSaved && <Notice tone="ok" testId="pin-saved">{t('children.pinSaved')}</Notice>}
    </SoftCard>
  );

  const addButton = (
    <button
      type="button"
      data-testid="add-child"
      onClick={() => { setAdding(true); setErrorKey(null); setCreated(false); }}
      className={cn(pill.base, pill.md, pill.primary)}
    >
      <Plus className="h-5 w-5" />
      {t('children.add')}
    </button>
  );
  const empty = !loading && children.length === 0;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      {/* One main action, «أضف طفلاً» — in the header, or in the empty state
          when there is nobody yet. */}
      <PageHeader
        title={t('children.title')}
        subtitle={t('children.subtitle')}
        actions={!adding && !empty ? addButton : undefined}
      />

      {errorKey && <Notice tone="danger" testId="children-error">{t(errorKey)}</Notice>}
      {created && !adding && <Notice tone="ok" testId="child-created">{t('children.created')}</Notice>}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          {!hasPin && pinCard}

          {adding && (
            <SoftCard testId="child-form" className="space-y-5">
              <CardTitle icon={<Baby />}>{t('children.add')}</CardTitle>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block space-y-2 sm:col-span-2">
                  <span className="text-sm font-medium text-ink">{t('children.name')}</span>
                  <input
                    data-testid="child-name"
                    value={name}
                    onChange={(e) => setName(applyFormat('personName', e.target.value))}
                    {...inputAttrs('personName')}
                    placeholder={t('children.namePlaceholder')}
                    className={field}
                  />
                </label>
                <label className="block space-y-2">
                  <span className="text-sm font-medium text-ink">{t('children.age')}</span>
                  <input
                    data-testid="child-age"
                    value={age}
                    onChange={(e) => setAge(applyFormat('age', e.target.value))}
                    placeholder={t('children.agePlaceholder')}
                    {...inputAttrs('age')}
                    className={field}
                  />
                </label>
                <fieldset className="space-y-2">
                  <legend className="mb-2 text-sm font-medium text-ink">{t('children.gender')}</legend>
                  <ChoicePills
                    value={gender}
                    onChange={setGender}
                    options={(['male', 'female'] as const).map((g) => ({
                      value: g, label: t(`auth.nameStep.${g}`), testId: `child-gender-${g}`,
                    }))}
                  />
                </fieldset>
                <label className="block space-y-2">
                  <span className="text-sm font-medium text-ink">{t('children.country')}</span>
                  <select
                    data-testid="child-country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className={field}
                  >
                    <option value="">—</option>
                    <option value="SA">SA</option>
                  </select>
                </label>
                <label className="block space-y-2">
                  <span className="text-sm font-medium text-ink">{t('children.grade')}</span>
                  <select
                    data-testid="child-grade"
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                    className={field}
                  >
                    <option value="">—</option>
                    {Array.from({ length: 12 }, (_, i) => String(i + 1)).map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </label>
              </div>

              {/*
                * Never pre-ticked. This is the record the server writes against a
                * person who cannot consent for themselves, and a box that arrives
                * already ticked is not a statement the guardian made.
                */}
              <label className="flex items-start gap-3 rounded-[14px] bg-surface p-4">
                <input
                  type="checkbox"
                  data-testid="child-consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-5 w-5 shrink-0 accent-primary"
                />
                <span className="text-sm text-ink">{t('children.consent')}</span>
              </label>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="save-child"
                  onClick={() => { void save(); }}
                  disabled={busy}
                  aria-busy={busy}
                  className={cn(pill.base, pill.md, pill.primary, 'min-w-[140px]')}
                >
                  {busy ? t('common.saving') : t('children.save')}
                </button>
                <button
                  type="button"
                  data-testid="cancel-child"
                  onClick={() => { setAdding(false); reset(); setErrorKey(null); }}
                  className={cn(pill.base, pill.md, pill.quiet)}
                >
                  {t('children.cancel')}
                </button>
              </div>
            </SoftCard>
          )}

          {loading ? (
            <div data-testid="children-loading" className="card-soft h-32 animate-pulse" />
          ) : children.length === 0 ? (
            !adding && (
              <SoftCard as="div">
                <EmptyState
                  testId="no-children"
                  icon={<Users />}
                  title={t('children.emptyTitle')}
                  body={t('children.emptyBody')}
                  action={addButton}
                />
              </SoftCard>
            )
          ) : (
            <ul className="grid items-start gap-4 sm:grid-cols-2">
              {children.map((child: Child) => (
                <ChildCard key={child.uid} child={child} />
              ))}
            </ul>
          )}
        </div>

        <aside className="space-y-5">
          {hasPin && pinCard}

          {/* م-3-01: following a pupil whose account somebody else owns — by a
              code their side issued, never by knowing a phone number. */}
          <SoftCard testId="link-section" className="space-y-4">
            <CardTitle icon={<Link2 />}>{t('children.linkTitle')}</CardTitle>
            <p className="text-sm text-helper">{t('children.linkWhy')}</p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                data-testid="invite-code"
                value={inviteCode}
                onChange={(e) => { setInviteCode(applyFormat('inviteCode', e.target.value)); setLinked(false); }}
                placeholder={t('children.linkPlaceholder')}
                {...inputAttrs('inviteCode')}
                dir="ltr"
                className={cn(field, 'w-44 text-center uppercase tracking-[0.3em]')}
              />
              <button
                type="button"
                data-testid="redeem-code"
                onClick={() => { void follow(); }}
                disabled={linkBusy}
                aria-busy={linkBusy}
                className={cn(pill.base, pill.md, pill.lime)}
              >
                {linkBusy ? t('common.saving') : t('children.linkAction')}
              </button>
            </div>
            {linkErrorKey && <Notice tone="danger" testId="link-error">{t(linkErrorKey)}</Notice>}
            {linked && <Notice tone="ok" testId="linked">{t('children.linked')}</Notice>}
          </SoftCard>

          {/* A second guardian is invited from the profile, where the code is
              issued. */}
          <Link
            to="/profile"
            className="card-soft flex items-center gap-3 p-4 transition-colors hover:bg-surface"
          >
            <IconTile><UserPlus /></IconTile>
            <span className="flex-1 font-medium text-ink">{t('children.inviteAnother')}</span>
            <ChevronLeft className="h-5 w-5 text-helper ltr:rotate-180" />
          </Link>
        </aside>
      </div>
    </div>
  );
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * One child: who, how this week went, and the way into their progress
 * (board f1). The week is read from the child's own recitations, which the
 * rules allow only to their parent.
 */
function ChildCard({ child }: { child: Child }) {
  const { t } = useLanguage();
  const { recitations, loading } = useChildRecitations(child.uid);
  const since = Date.now() - WEEK_MS;
  const thisWeek = recitations.filter((r) => r.endedAt >= since).length;
  const count = (n: number) => new Intl.NumberFormat('en-US').format(n);

  const facts = [
    ...(child.age === null ? [] : [count(child.age)]),
    child.grade === null ? t('children.noGrade') : t('children.gradeLabel', { grade: count(child.grade) }),
    ...(loading ? [] : [thisWeek === 0
      ? t('children.noneThisWeek')
      : t('children.thisWeek', { count: thisWeek, n: count(thisWeek) })]),
  ];

  return (
    <li data-testid={`child-${child.uid}`} className="card-soft flex flex-col gap-4 p-5">
      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-lime text-accent-foreground">
          <Baby className="h-6 w-6" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[17px] font-semibold text-ink">{child.displayName}</p>
          <p data-testid={`child-${child.uid}-facts`} className="text-sm text-helper">{facts.join(' · ')}</p>
        </div>
        {/* Owning an account and following one are different powers, so the
            card says which this is. */}
        {child.relation === 'guardian' && (
          <QuietPill testId={`followed-${child.uid}`}>{t('children.followedOnly')}</QuietPill>
        )}
      </div>

      {/* Only the OWNER may edit the child's record (validChildEdit). */}
      {child.gender === null && child.relation === 'owner' && (
        <GenderPrompt
          message={t('children.genderNeeded', { name: child.displayName })}
          onChoose={(g) => setChildGender(child.uid, g)}
          testIdPrefix={`child-${child.uid}-gender`}
        />
      )}

      <Link
        to={`/children/${child.uid}`}
        data-testid={`open-${child.uid}`}
        className={cn(pill.base, pill.md, pill.lime, 'self-start')}
      >
        {t('children.viewProgress')}
        <ChevronLeft className="h-4 w-4 ltr:rotate-180" />
      </Link>
    </li>
  );
}
